import { t } from "ttag";
import { z } from "zod";

import { Category, PAYMENT_METHOD_TYPE } from "@/shared/domains";
import {
  answerSchema,
  availableLanguageSchema,
  imperialBmiInputsSchema,
  trimmedEmail,
  userAttributesSchema,
} from "@/shared/models";
import {
  phoneNumberSchema,
  zipCodeSchema,
} from "@/shared/models/legacy-schemas";
import { SIGNUP_FUNNEL } from "./constants";

const addressSchema = z.object({
  address: z.string().min(1, { error: () => t`Address is required` }),
  apartmentNumber: z.string(),
  zipCode: zipCodeSchema,
  city: z.string().min(1, { error: () => t`City is required` }),
});

export const contactInfoSchema = z.object({
  firstName: z.string().min(1, { error: () => t`First name is required` }),
  lastName: z.string().min(1, { error: () => t`Last name is required` }),
  email: trimmedEmail,
  shipping: addressSchema,
  billing: addressSchema,
  phoneNumber: phoneNumberSchema,
  smsMarketingConsent: z.boolean().default(false).optional(),
  emailMarketingConsent: z.boolean().default(true).optional(),
});

export const createUserSchema = contactInfoSchema
  .omit({ shipping: true, billing: true })
  .and(imperialBmiInputsSchema.partial())
  .and(
    z.object({
      eligibilityAnswerIds: z.array(answerSchema.shape.id),
    }),
  )
  .and(
    z.object({
      productCategory: z.string().optional(),
      productSlug: z.string().optional(),
      billingFrequency: z.number().optional(),
    }),
  )
  .and(
    z.object({
      funnel: z.enum(SIGNUP_FUNNEL).optional(),
      priceVariant: z.string().optional(),
    }),
  )
  .and(
    z.object({
      gender: userAttributesSchema.shape.gender.optional(),
      dateOfBirth: z.string().optional(),
    }),
  );

export const onboardingUserSchema = contactInfoSchema.extend({
  ehrId: z.uuid().nullable(),
  code: z.uuid(),
  state: z.string().min(1, { error: () => t`State is required` }),
  token: z.uuid().optional(),
  affiliateId: z.string().nullable().optional(),
  efTransactionId: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
  uid: z.string().nullable().optional(),
  sub1: z.string().nullable().optional(),
  sub2: z.string().nullable().optional(),
  sub3: z.string().nullable().optional(),
  sub4: z.string().nullable().optional(),
  sub5: z.string().nullable().optional(),
});

export const checkoutSchema = z
  .object({
    creditCard: z.boolean().optional(),
    authorization: z.boolean().optional(),
    couponCode: z.string().optional(),
    paymentMethodType: z.enum(PAYMENT_METHOD_TYPE),
  })
  .superRefine((data, ctx) => {
    if (data.paymentMethodType === PAYMENT_METHOD_TYPE.CARD) {
      if (!data.creditCard) {
        ctx.addIssue({
          code: "custom",
          message: t`Credit card is required`,
          path: ["creditCard"],
        });
      }

      if (!data.authorization) {
        ctx.addIssue({
          code: "custom",
          message: t`Authorization is required`,
          path: ["authorization"],
        });
      }
    }
  });

export const createHealthieUserParamsSchema = z.object({
  userCode: z.string(),
  providerId: z.string().optional(),
  line1: z.string().min(1),
  line2: z.string().optional(),
  zip: z.string().min(1),
  state: z.string().nullable(),
  city: z.string().min(1),
  phoneNumber: phoneNumberSchema,
  language: availableLanguageSchema.optional(),
});

export const productCategoriesParamsSchema = userAttributesSchema.extend({
  dateOfBirth: z.iso.date(),
});

export const categoryEligibilitySchema = z.object({
  category: Category.omit({ treatments: true }),
  disqualified: z.boolean(),
  hasEligibilityQuestions: z.boolean(),
});
